var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TableBody } from '../../common';
import { QueueType } from '../../multiskill/types';
import { PLAN_COLORS } from '../../wizard/constants';
import { COST_ROWS, CURRENCY, HIDE_ROW, ONE_DECIMAL_VALUE, ONLY_PLAN_DATA, ONLY_SINGLE_QUEUE, PERCENTAGES, PERIOD_MAIN_ROW_SUMMARY_AVERAGE_KEYS, PERIOD_REPORT_ROWS, QUEUE_ONLY_PERIOD_HIERARCHY_ROWS, STAFF_REQUIREMENT_ROWS, TOTAL_DATA, getCurrencyDecimalValue } from '../constants';
import { usePeriodReportChunkSize } from '../selectors';
import { MainRow } from './mainRow';
import { QueueRow } from './queueRow';
import { INDENT_LEVELS, areAllOccupancyValues100Percent, getSeatsRequiredPeak } from './utils';
export { MainRow } from './mainRow';
export { QueueRow } from './queueRow';
export var getSeatsSummary = function (data, chunkSize, countryOnly) {
  var _a, _b, _c, _d, _e, _f;
  if (countryOnly === void 0) {
    countryOnly = false;
  }
  var hasLocationData = Boolean(((_a = data.countries) === null || _a === void 0 ? void 0 : _a.some(function (country) {
    return country.locations.length > 0;
  })) || ((_b = data.queues) === null || _b === void 0 ? void 0 : _b.some(function (queue) {
    var _a;
    return (_a = queue.countries) === null || _a === void 0 ? void 0 : _a.some(function (country) {
      return country.locations.length > 0;
    });
  })));
  if (chunkSize <= 0) return undefined;
  // The backend has already assigned ownership-adjusted peaks to each queue.
  var locationRows = countryOnly ? __spreadArray(__spreadArray([], (_c = data.countries) !== null && _c !== void 0 ? _c : [], true), ((_d = data.queues) !== null && _d !== void 0 ? _d : []).filter(function (queue) {
    return queue.dataType === QueueType.Multiskill;
  }), true) : (_e = data.queues) !== null && _e !== void 0 ? _e : [];
  var rows = hasLocationData ? locationRows : (_f = data.queues) !== null && _f !== void 0 ? _f : [];
  if (!rows.length) return undefined;
  return Array.from({
    length: Math.ceil(data.periodLength / chunkSize)
  }, function (_, year) {
    var start = year * chunkSize;
    return rows.reduce(function (total, row) {
      return total + getSeatsRequiredPeak(row, start, start + chunkSize);
    }, 0);
  });
};
var DEFAULT_ADD_COLUMNS = ['Average'];
var PROD_HOURS_HIDE_ROWS = new Set(['productiveHours', 'shrinkage', 'attrition', 'productionFte', 'requiredFte', 'requiredHeadcount']);
var PROD_HOURS_SUMMARY_TOTAL_ROWS = new Set(['productiveHours', 'productionHours']);
var getDefaultShowDetails = function (rowItem) {
  var firstItem = rowItem.find(function (item) {
    return !TOTAL_DATA.includes(item);
  });
  return firstItem ? [firstItem] : [];
};
var toggleItem = function (items, item) {
  return items.includes(item) ? items.filter(function (i) {
    return i !== item;
  }) : __spreadArray(__spreadArray([], items, true), [item], false);
};
export var PeriodSectionBody = function (_a) {
  var _b;
  var data = _a.data,
    _c = _a.addColumns,
    addColumns = _c === void 0 ? DEFAULT_ADD_COLUMNS : _c,
    _d = _a.rowItem,
    rowItem = _d === void 0 ? STAFF_REQUIREMENT_ROWS : _d,
    _e = _a.extendItem,
    extendItem = _e === void 0 ? 'supportStaff' : _e,
    _f = _a.adjustItem,
    adjustItem = _f === void 0 ? 'totalSupportStaff' : _f,
    _g = _a.isProdHoursPlan,
    isProdHoursPlan = _g === void 0 ? false : _g,
    _h = _a.countryOnly,
    countryOnly = _h === void 0 ? false : _h,
    _j = _a.totalExtendAsParent,
    totalExtendAsParent = _j === void 0 ? false : _j,
    expandedItems = _a.expandedItems,
    onToggleItem = _a.onToggleItem;
  var _k = useState(function () {
      return getDefaultShowDetails(rowItem);
    }),
    localShowDetails = _k[0],
    setLocalShowDetails = _k[1];
  var t = useTranslation('report', {
    keyPrefix: 'period.reportRows'
  }).t;
  var chunkSize = usePeriodReportChunkSize();
  var isDetailShown = function (item) {
    var _a;
    if (expandedItems !== undefined) return (_a = expandedItems[item]) !== null && _a !== void 0 ? _a : false;
    return localShowDetails.includes(item);
  };
  var toggleLocalShowDetails = function (item) {
    setLocalShowDetails(function (prev) {
      return toggleItem(prev, item);
    });
  };
  var onShowRow = function (item) {
    React.startTransition(function () {
      if (onToggleItem) return onToggleItem(item);
      toggleLocalShowDetails(item);
    });
  };
  var getAveragePrecision = function (item) {
    if (item === 'requiredHeadcount') return 0;
    if (ONE_DECIMAL_VALUE.includes(item)) return 1;
    return getCurrencyDecimalValue(item);
  };
  var getMainRowSummaryValue = function (item) {
    if (isProdHoursPlan && PROD_HOURS_SUMMARY_TOTAL_ROWS.has(item)) return 'total';
    if (item === 'seatsRequired') return 'max';
    if (item === 'forecastedDemand') return 'total';
    return 'avg';
  };
  var getHideRowsForPlan = function (item) {
    return HIDE_ROW.includes(item) || isProdHoursPlan && PROD_HOURS_HIDE_ROWS.has(item);
  };
  var isIncludeTotal = addColumns === null || addColumns === void 0 ? void 0 : addColumns.includes('Total');
  var showCountryHierarchy = countryOnly && !!((_b = data.countries) === null || _b === void 0 ? void 0 : _b.length);
  var shouldHideProductiveHoursMainRow = React.useMemo(function () {
    if (!isProdHoursPlan || !data.queues || data.queues.length === 0) return false;
    return data.queues.every(function (queue) {
      if (!queue.occupancy || !Array.isArray(queue.occupancy)) return false;
      return areAllOccupancyValues100Percent(queue.occupancy);
    });
  }, [isProdHoursPlan, data.queues]);
  var getMainRowData = function (item) {
    if (item === extendItem) {
      var adjustData = data[adjustItem];
      return Array.isArray(adjustData) ? adjustData : undefined;
    }
    var itemData = data[item];
    if (!itemData) return undefined;
    return Array.isArray(itemData) ? itemData : undefined;
  };
  var getMainRowCalculated = function (item) {
    var _a;
    if (item === 'seatsRequired') return getSeatsSummary(data, chunkSize, countryOnly);
    if (PERIOD_MAIN_ROW_SUMMARY_AVERAGE_KEYS.includes(item)) return (_a = data.summary) === null || _a === void 0 ? void 0 : _a[item];
    return undefined;
  };
  return _jsx(TableBody, {
    children: rowItem.filter(function (item) {
      return !TOTAL_DATA.includes(item);
    }).map(function (item) {
      var _a;
      var isMultiskill = data.dataType === QueueType.Multiskill && !ONLY_SINGLE_QUEUE.includes(item);
      var isHideRow = getHideRowsForPlan(item);
      var titleKey = !isProdHoursPlan && PERIOD_REPORT_ROWS[item] === PERIOD_REPORT_ROWS.occupancy ? PERIOD_REPORT_ROWS.occOrProd : PERIOD_REPORT_ROWS[item];
      var shouldHideMainRow = item === 'productiveHours' && shouldHideProductiveHoursMainRow;
      var showDetails = isDetailShown(item);
      var rowColor = (_a = PLAN_COLORS[data === null || data === void 0 ? void 0 : data.colorIndex]) === null || _a === void 0 ? void 0 : _a[1];
      if (item === extendItem) {
        var extendedData_1 = data[item];
        var roles = Object.keys(extendedData_1 !== null && extendedData_1 !== void 0 ? extendedData_1 : {});
        var hasRoles = roles.length > 0;
        var totalRow = !shouldHideMainRow && _jsx(MainRow, {
          bold: true,
          data: getMainRowData(item),
          rowColor: rowColor,
          title: t(PERIOD_REPORT_ROWS[adjustItem]),
          isHideRow: totalExtendAsParent,
          showDetails: isDetailShown(adjustItem),
          onShowRow: hasRoles ? function () {
            return onShowRow(adjustItem);
          } : undefined,
          isIncludeTotal: isIncludeTotal,
          averagePrecision: getAveragePrecision(item),
          percentage: PERCENTAGES.includes(item),
          isCurrency: CURRENCY.includes(item),
          calculated: getMainRowCalculated(item),
          isMultiskill: isMultiskill
        });
        var roleRows = roles.map(function (role) {
          return _jsxs(React.Fragment, {
            children: [_jsx(MainRow, {
              bold: true,
              data: extendedData_1[role],
              title: role,
              rowColor: rowColor,
              summaryColor: rowColor,
              averagePrecision: getAveragePrecision(item),
              isCurrency: CURRENCY.includes(item),
              isIncludeTotal: isIncludeTotal,
              isMultiskill: isMultiskill,
              nestLevel: totalExtendAsParent ? INDENT_LEVELS.lob : undefined,
              isHideRow: true,
              showDetails: isDetailShown(role),
              onShowRow: function () {
                return onShowRow(role);
              }
            }), isDetailShown(role) && _jsx(QueueRow, {
              data: data.queues,
              dataWithLob: data.queuesByLob,
              isIncludeTotal: isIncludeTotal,
              keyItem: item,
              rowColor: rowColor,
              role: role,
              averagePrecision: getAveragePrecision(item),
              isCurrency: CURRENCY.includes(item),
              isMultiskill: isMultiskill,
              summaryValue: getMainRowSummaryValue(item),
              lobIndent: totalExtendAsParent ? INDENT_LEVELS.queue : undefined,
              queueIndent: totalExtendAsParent ? INDENT_LEVELS.country : undefined,
              countryIndent: totalExtendAsParent ? INDENT_LEVELS.location : undefined,
              locationIndent: totalExtendAsParent ? INDENT_LEVELS.supportStaffLocation : undefined,
              expandedItems: expandedItems,
              onToggleItem: onToggleItem
            })]
          }, role);
        });
        var rows = totalExtendAsParent ? _jsxs(_Fragment, {
          children: [totalRow, isDetailShown(adjustItem) && roleRows]
        }) : _jsxs(_Fragment, {
          children: [roleRows, totalRow]
        });
        return _jsx(React.Fragment, {
          children: rows
        }, item);
      }
      return _jsxs(React.Fragment, {
        children: [!COST_ROWS.includes(item) && !shouldHideMainRow && _jsx(MainRow, {
          bold: true,
          data: getMainRowData(item),
          rowColor: rowColor,
          title: t(titleKey),
          isHideRow: isHideRow,
          isIncludeTotal: isIncludeTotal,
          averagePrecision: getAveragePrecision(item),
          percentage: PERCENTAGES.includes(item),
          isCurrency: CURRENCY.includes(item),
          onShowRow: function () {
            return onShowRow(item);
          },
          showDetails: showDetails,
          calculated: getMainRowCalculated(item),
          summaryValue: getMainRowSummaryValue(item),
          isMultiskill: isMultiskill
        }), (showDetails || !isHideRow) && _jsx(_Fragment, {
          children: !ONLY_PLAN_DATA.includes(item) && _jsx(QueueRow, {
            data: data.queues,
            dataWithLob: data.queuesByLob,
            countries: showCountryHierarchy ? data.countries : undefined,
            isIncludeTotal: isIncludeTotal,
            keyItem: item,
            rowColor: rowColor,
            averagePrecision: getAveragePrecision(item),
            percentage: PERCENTAGES.includes(item),
            isCurrency: CURRENCY.includes(item),
            isMultiskill: isMultiskill,
            summaryValue: getMainRowSummaryValue(item),
            disableHierarchy: QUEUE_ONLY_PERIOD_HIERARCHY_ROWS.includes(item),
            countryOnly: showCountryHierarchy,
            expandedItems: expandedItems,
            onToggleItem: onToggleItem
          })
        })]
      }, item);
    })
  });
};